import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { recruiterApi } from '../services/api'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AlertCircle, Calendar, Trash2, Loader2, UserCog, Plus, X } from 'lucide-react'

interface ProfessionalManager {
  id: number
  uuid: string
  recruiter_id: number
  name: string
  email: string
  created_at: string
}

export default function ProfessionalManagersPage() {
  const { recruiter } = useAuth()
  const [managers, setManagers] = useState<ProfessionalManager[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [managerToDelete, setManagerToDelete] = useState<ProfessionalManager | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null)

  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const fetchManagers = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await recruiterApi.getProfessionalManagers()
      if (response.status === 'success') {
        setManagers(response.professional_managers)
      } else {
        setError('שגיאה בטעינת מנהלים מקצועיים')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה בטעינת מנהלים מקצועיים')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchManagers()
  }, [recruiter])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('he-IL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim() || !newEmail.trim()) {
      setError('נא למלא שם ואימייל')
      return
    }
    try {
      setIsCreating(true)
      setError(null)
      const response = await recruiterApi.createProfessionalManager(newName.trim(), newEmail.trim())
      if (response.status === 'success') {
        setNewName('')
        setNewEmail('')
        setShowAddDialog(false)
        await fetchManagers()
      } else {
        setError(response.message || 'שגיאה ביצירת מנהל')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה ביצירת מנהל')
    } finally {
      setIsCreating(false)
    }
  }

  const handleAddDialogClose = () => {
    setShowAddDialog(false)
    setNewName('')
    setNewEmail('')
  }

  const handleDeleteClick = (manager: ProfessionalManager) => {
    setManagerToDelete(manager)
  }

  const handleDeleteConfirm = async () => {
    if (!managerToDelete) return
    try {
      setIsDeleting(true)
      setError(null)
      await recruiterApi.deleteProfessionalManager(managerToDelete.id)
      setManagerToDelete(null)
      await fetchManagers()
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'שגיאה במחיקת המנהל')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setManagerToDelete(null)
  }

  if (isLoading) {
    return (
      <div className="bg-background p-6 flex items-center justify-center min-h-[calc(100vh-96px)]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="bg-background p-6 flex flex-col min-h-[calc(100vh-96px)]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">מנהלים מקצועיים</h1>
          <p className="text-muted-foreground mt-1">
            מנהלים שיכולים לקבל שאלונים שמולאו במייל
          </p>
        </div>
        <div className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-muted-foreground" />
          <span className="text-lg font-semibold">{managers.length} מנהלים</span>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50 mt-4">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Managers list */}
      <div className="flex-1 mt-4 min-h-0">
        {managers.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <UserCog className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium text-muted-foreground">
                  אין מנהלים מקצועיים
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  לחץ על &quot;הוסף מנהל&quot; למטה כדי להוסיף. משאלונים שנשלחו תוכל לשלוח אליו שאלונים שמולאו במייל.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="flex flex-col max-h-full overflow-hidden">
            <CardHeader>
              <CardTitle>רשימת מנהלים מקצועיים</CardTitle>
            </CardHeader>
            <CardContent className="min-h-0 overflow-auto">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-right p-4 font-semibold">שם</th>
                      <th className="text-right p-4 font-semibold">אימייל</th>
                      <th className="text-right p-4 font-semibold">תאריך הוספה</th>
                      <th className="text-right p-4 font-semibold w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((manager) => (
                      <tr
                        key={manager.id}
                        className="border-b hover:bg-muted/50 transition-colors"
                        onMouseEnter={() => setHoveredRowId(manager.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                      >
                        <td className="p-4 font-medium">{manager.name}</td>
                        <td className="p-4 text-muted-foreground">{manager.email}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span>{formatDate(manager.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-4 w-16">
                          <Button
                            variant="ghost"
                            size="icon"
                            className={`h-8 w-8 transition-opacity duration-200 ${
                              hoveredRowId === manager.id
                                ? 'opacity-100 visible text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                                : 'opacity-0 invisible'
                            }`}
                            onClick={() => handleDeleteClick(manager)}
                            title="מחק מנהל"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom left: Add manager button */}
      <div className="mt-4 flex justify-end">
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          הוסף מנהל
        </Button>
      </div>

      {/* Add manager dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>הוסף מנהל מקצועי</CardTitle>
                <Button variant="ghost" size="icon" onClick={handleAddDialogClose} disabled={isCreating}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">שם</label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="שם המנהל"
                    disabled={isCreating}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">אימייל</label>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@example.com"
                    disabled={isCreating}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={handleAddDialogClose} disabled={isCreating}>
                    ביטול
                  </Button>
                  <Button type="submit" disabled={isCreating || !newName.trim() || !newEmail.trim()}>
                    {isCreating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        מוסיף...
                      </>
                    ) : (
                      'הוסף מנהל'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {managerToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>מחיקת מנהל מקצועי</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                האם אתה בטוח שברצונך למחוק את המנהל <strong>{managerToDelete.name}</strong> ({managerToDelete.email})?
              </p>
              <p className="text-xs text-muted-foreground">
                פעולה זו לא ניתנת לביטול.
              </p>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={handleDeleteCancel} disabled={isDeleting}>
                  ביטול
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="text-slate-700 border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      מוחק...
                    </>
                  ) : (
                    'מחק'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
